import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft, ChevronLeft, ChevronRight, Shield, User, FileText, Settings, LogIn, RefreshCw, Upload, Eye } from 'lucide-react';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const ACTION_ICONS = {
  login: LogIn,
  create_assessment: FileText,
  create_user: User,
  update_user: User,
  update_settings: Settings,
  upload_logo: Upload,
  view_report: Eye,
  regenerate_ai: RefreshCw,
};

const ACTION_LABELS = {
  login: 'Logged In',
  create_assessment: 'Created Assessment',
  create_user: 'Created User',
  update_user: 'Updated User',
  update_settings: 'Updated Settings',
  upload_logo: 'Uploaded Logo',
  view_report: 'Viewed Report',
  regenerate_ai: 'Regenerated AI Analysis',
};

const ACTION_COLORS = {
  login: 'bg-blue-50 text-blue-700 border-blue-200',
  create_assessment: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  create_user: 'bg-violet-50 text-violet-700 border-violet-200',
  update_user: 'bg-amber-50 text-amber-700 border-amber-200',
  update_settings: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  upload_logo: 'bg-teal-50 text-teal-700 border-teal-200',
  view_report: 'bg-sky-50 text-sky-700 border-sky-200',
  regenerate_ai: 'bg-purple-50 text-purple-700 border-purple-200',
};

const AuditLogs = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('');
  const [availableActions, setAvailableActions] = useState([]);

  useEffect(() => {
    fetchActions();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, filterAction, filterUser]);

  const fetchActions = async () => {
    try {
      const res = await axios.get(`${API_URL}/audit-logs/actions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableActions(res.data.actions || []);
    } catch {}
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (filterAction !== 'all') params.append('action', filterAction);
      if (filterUser.trim()) params.append('user_email', filterUser.trim());

      const res = await axios.get(`${API_URL}/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(res.data.logs || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (ts) => {
    const d = new Date(ts);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const getDetailsSummary = (log) => {
    const d = log.details || {};
    if (log.action === 'create_assessment') return `${d.candidate || ''} - ${d.role || ''} (${d.version || ''})`;
    if (log.action === 'create_user') return `${d.email || ''} (${d.role || ''})`;
    if (log.action === 'update_user') return Object.keys(d).join(', ');
    if (log.action === 'update_settings') return Object.keys(d).join(', ');
    if (log.action === 'upload_logo') return d.filename || '';
    if (log.action === 'view_report') return d.candidate || '';
    if (log.action === 'regenerate_ai') return d.candidate || '';
    if (log.action === 'login') return d.role || '';
    return '';
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <div className="max-w-6xl mx-auto p-8">
        <Button onClick={() => navigate('/dashboard')} variant="outline" className="rounded-md mb-4" data-testid="back-button">
          <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
        </Button>

        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="audit-title">
              Audit Log
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              {total} total events recorded
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-brand" />
            <span className="text-sm text-neutral-600 font-medium">Full Admin Activity Trail</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <Input
            placeholder="Filter by user email..."
            value={filterUser}
            onChange={(e) => { setFilterUser(e.target.value); setPage(1); }}
            className="max-w-xs rounded-md"
            data-testid="filter-user-input"
          />
          <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); setPage(1); }}>
            <SelectTrigger className="w-[200px] rounded-md" data-testid="filter-action-select">
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {availableActions.map((a) => (
                <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-neutral-200 shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-neutral-500">Loading...</TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-neutral-500">No audit events found.</TableCell>
                </TableRow>
              ) : logs.map((log) => {
                const Icon = ACTION_ICONS[log.action] || Shield;
                const color = ACTION_COLORS[log.action] || 'bg-neutral-100 text-neutral-600 border-neutral-200';
                return (
                  <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                    <TableCell className="text-xs text-neutral-500 font-mono">
                      {formatTimestamp(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium text-neutral-900">{log.user_name}</p>
                        <p className="text-xs text-neutral-500">{log.user_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${color}`}>
                        <Icon className="h-3 w-3" />
                        {ACTION_LABELS[log.action] || log.action}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-neutral-600 font-mono bg-neutral-50 px-1.5 py-0.5 rounded">
                        {log.resource_type}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-neutral-600">{getDetailsSummary(log)}</span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-4">
            <p className="text-sm text-neutral-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="rounded-md"
                data-testid="prev-page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="rounded-md"
                data-testid="next-page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
